import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import {
  detectLocale,
  persistLocale,
  translate,
  type Locale,
  type TranslationKey,
} from './i18n.js';
import { api, type SessionResponse } from './lib/api.js';
import {
  applyTheme,
  currentMode,
  persistMode,
  themeFor,
  watchSystemTheme,
  type Theme,
  type ThemeMode,
} from './lib/theme.js';
import { Header } from './components/Header.js';
import { Help } from './components/Help.js';
import { Admin } from './pages/Admin.js';
import { ErrorBoundary, Loading } from './components/ui.js';
import { Jobs } from './pages/Jobs.js';
import { Login } from './pages/Login.js';
import { Summary } from './pages/Summary.js';
import { Decompte } from './pages/Decompte.js';
import { Tabs } from './components/Tabs.js';

export function App() {
  const [locale, setLocale] = useState<Locale>(detectLocale);
  const [mode, setModeState] = useState<ThemeMode>(currentMode);
  const [theme, setTheme] = useState<Theme>(() => themeFor(currentMode()));
  const [session, setSession] = useState<SessionResponse | null>(null);
  // `?auth=expired|failed` : posé par le retour d'Entra quand la connexion n'aboutit pas.
  const [logoutFailed, setLogoutFailed] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [authReason] = useState<string | null>(() =>
    new URLSearchParams(location.search).get('auth'),
  );
  const [version, setVersion] = useState<string | null>(null);

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>) => translate(locale, key, vars),
    [locale],
  );

  useEffect(() => {
    document.documentElement.lang = locale;
    persistLocale(locale);
  }, [locale]);

  useEffect(() => applyTheme(theme), [theme]);

  // Tant que personne n'a choisi, l'application suit le système en direct.
  useEffect(() => watchSystemTheme(setTheme), []);

  const chooseMode = useCallback((next: ThemeMode) => {
    persistMode(next);
    setModeState(next);
    setTheme(themeFor(next));
  }, []);

  useEffect(() => {
    api
      .session()
      .then(setSession)
      .catch(() => setSession(null));
    // Le poll de version est une fonction secondaire : son échec ne notifie personne.
    api
      .health()
      .then((h) => setVersion(h.version))
      .catch(() => undefined);
  }, []);

  const changePersona = useCallback(async (persona: string) => {
    await api.switchPersona(persona);
    setSession(await api.session());
  }, []);

  const logout = useCallback(async () => {
    // Se déconnecter de Mnemo ne ferme QUE la session Mnemo : on ne propose pas de
    // quitter Microsoft 365, qui emporterait Outlook, Teams et le reste.
    //
    // ⚠️ Ne JAMAIS avaler l'échec. Si l'appel rate, la session n'est pas détruite — et
    // recharger la page donnerait alors toutes les apparences d'une déconnexion réussie
    // alors que la personne est toujours connectée. C'est exactement le piège où nous
    // sommes tombés : un `.catch()` silencieux masquait une erreur 500.
    try {
      await api.logout();
      // Retour à l'accueil, et rien de plus : la session Microsoft 365 reste ouverte.
      location.href = '/';
    } catch {
      setLogoutFailed(true);
    }
  }, []);

  const boundaryLabels = useMemo(
    () => ({ title: t('boundary.title'), body: t('boundary.body'), reload: t('boundary.reload') }),
    [t],
  );

  return (
    <ErrorBoundary {...boundaryLabels}>
      <BrowserRouter>
        <div className="min-h-screen">
          {logoutFailed && (
            <p role="alert" className="bg-danger/15 px-4 py-2 text-center text-sm">
              <span aria-hidden="true">⚠ </span>
              {t('session.logoutFailed')}
            </p>
          )}
          <Header
            t={t}
            session={session}
            locale={locale}
            mode={mode}
            version={version}
            onLocale={setLocale}
            onMode={chooseMode}
            onPersona={(p) => void changePersona(p)}
            onHelp={() => setHelpOpen(true)}
            onLogout={() => void logout()}
          />
          {session && !session.user && !session.stubAuth ? (
            <Login t={t} reason={authReason} />
          ) : session ? (
            <>
              <Tabs t={t} locale={locale} userId={session.user?.id} />
              <Routes>
                <Route path="/synthese" element={<Summary t={t} locale={locale} />} />
                <Route
                  path="/decompte"
                  element={<Decompte t={t} locale={locale} session={session} />}
                />
                {session.user?.isAdmin && (
                  <Route path="/admin/*" element={<Admin t={t} locale={locale} />} />
                )}
                <Route path="*" element={<Jobs t={t} locale={locale} session={session} />} />
              </Routes>
            </>
          ) : (
            <Loading t={t} />
          )}
          {helpOpen && <Help t={t} onClose={() => setHelpOpen(false)} />}
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
